"use strict";
/**
 * engineType → NodeTypeDefinition slug resolver. Shared frontend + backend.
 *
 * SWR canvas speaks engine types (`swr.ai`, `swr.code`, …). Panel + insert-
 * seed consume NodeTypeDefinitions keyed by slug (`ai-text`, `code`, …).
 * One shared mapping avoids drift between the two sides.
 *
 * 0 Fallbacks: throws on unknown engine types. A missing mapping is a
 * programming error, not user-visible.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveNodeTypeSlug = resolveNodeTypeSlug;
exports.resolveNodeTypeSlugFromType = resolveNodeTypeSlugFromType;
const AI_CAPABILITY_SLUGS = {
    chat: 'ai-text',
    vision: 'ai-vision',
    image: 'ai-image',
    // sl-67kc.1.5: STT is always the async submit/suspend node now.
    stt: 'ai-stt-submit',
    webSearch: 'ai-web-search',
};
const DIRECT_SLUGS = {
    'swr.code': 'code',
    'swr.http': 'http-request',
    'swr.branch_xor': 'control-branch_xor',
    'swr.branch_or': 'control-branch_or',
    'swr.branch_and': 'control-branch_and',
    'swr.loop': 'control-loop',
    'swr.merge': 'control-merge',
    'swr.split': 'control-split',
    'swr.filter': 'control-filter',
    'swr.join': 'control-join',
    'swr.output_sanitizer': 'data-output-sanitizer',
    'swr.json_extract': 'data-json-extract',
    'swr.start': 'interface-start',
    'swr.end': 'interface-end',
};
function resolveNodeTypeSlug(input) {
    const { engineType, config } = input;
    const explicitSlug = config['nodeTypeSlug'];
    if (typeof explicitSlug === 'string' && explicitSlug.trim().length > 0) {
        return explicitSlug.trim();
    }
    if (engineType === 'swr.ai') {
        if (config['agentMode'] === true || typeof config['agentId'] === 'string') {
            return 'ai-agent';
        }
        const capability = config['capability'] ?? 'chat';
        const slug = AI_CAPABILITY_SLUGS[capability];
        if (!slug) {
            throw new Error(`Unknown AI capability: ${capability}`);
        }
        return slug;
    }
    if (engineType === 'swr.service' || engineType === 'swr.media') {
        const slug = config['nodeTypeSlug'];
        if (!slug) {
            throw new Error(`${engineType} step missing nodeTypeSlug parameter — cannot resolve node type definition`);
        }
        return slug;
    }
    const direct = DIRECT_SLUGS[engineType];
    if (direct)
        return direct;
    throw new Error(`Unknown engine type: ${engineType}`);
}
/** Normalize bare (`ai`, `code`, …) or prefixed (`swr.ai`) type. */
function resolveNodeTypeSlugFromType(type, config) {
    const engineType = type.startsWith('swr.') ? type : `swr.${type}`;
    return resolveNodeTypeSlug({ engineType, config });
}
